import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LDFlagSet } from 'launchdarkly-js-client-sdk';

import { usePrevious } from '../../hooks/usePrevious';
import { useLaunchDarkly } from './LaunchDarklyProvider';
import type { FeatureFlagKey, FeatureFlags, FeatureFlagValue } from './types';

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Returns the value of a feature flag using the following sources (in that order):
 *
 * 1. Overrides (from apps/order/src/context/LaunchDarkly/LaunchDarkly.overrides.tsx)
 * 2. Actual values (from remote)
 * 3. Local defaults (from `./defaults.ts`)
 *
 * If none of these are available, `undefined` will be returned.
 *
 * The hook also supports optional live tracking, which ensures that the value is always up-to-date.
 */
export const useFeatureFlag = <Key extends FeatureFlagKey>(
  flagKey: Key,
  options?: UseFeatureFlagOptions,
) => {
  const { clientRef, isReady, defaults, overrides } = useLaunchDarkly();
  const { current: client } = clientRef;

  const [featureFlagValue, setFeatureFlagValue] = useState<
    FeatureFlagValue<Key>
  >(client?.variation?.(flagKey));

  const prevIsReady = usePrevious(isReady);
  const shouldGetActualValue = !prevIsReady && isReady;
  const shouldListenForChanges = options?.listenForChanges;

  // ─────────────────────────────────────────────────────────────────

  // retrieve the actual feature flag value, after initializing the client
  useEffect(() => {
    if (!shouldGetActualValue) return;

    setFeatureFlagValue(client?.variation?.(flagKey));
  }, [client, flagKey, shouldGetActualValue]);

  // listen for changes
  useEffect(() => {
    if (!client || !shouldListenForChanges) return;

    client.on(`change:${flagKey}`, setFeatureFlagValue);

    return () => {
      client.off(`change:${flagKey}`, setFeatureFlagValue);
    };
  }, [client, shouldListenForChanges, flagKey]);

  // ─────────────────────────────────────────────────────────────────

  return useMemo<FeatureFlagValue<Key>>(() => {
    const localOverride = overrides[flagKey];
    const localDefault = defaults[flagKey];

    return localOverride ?? featureFlagValue ?? localDefault;
  }, [defaults, overrides, flagKey, featureFlagValue]);
};

/**
 * Extends the functionality of the `useFeatureFlag` hook for advanced use cases.
 *
 * It returns an object with the following properties:
 *
 * - `value`: feature flag value with `undefined` as a fallback.
 * - `isStale`: whether the feature flag was returned by the initialized client.
 * - `isOverridden`: Indicates that the specified feature flag has been overridden locally.
 */
export const useFeatureFlagAsync = <Key extends FeatureFlagKey>(
  flagKey: Key,
  options?: UseFeatureFlagOptions,
) => {
  const { isReady, overrides } = useLaunchDarkly();

  const value = useFeatureFlag(flagKey, options);
  const isStale = !isReady;
  const isOverridden = Object.prototype.hasOwnProperty.call(overrides, flagKey);

  return useMemo<UseFeatureFlagAsyncReturnValue<Key>>(
    () => ({ value, isStale, isOverridden }),
    [value, isStale, isOverridden],
  );
};

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Returns the value of a feature flag using the following sources (in that order):
 *
 * 1. Local defaults (from `./defaults.ts`)
 * 2. Actual values (from remote)
 * 3. Overrides (from apps/order/src/context/LaunchDarkly/LaunchDarkly.overrides.tsx)
 *
 * If none of these are available, an empty object will be returned.
 *
 * The hook also supports optional live tracking, which ensures that the values are always up-to-date.
 */
export const useAllFeatureFlags = (options?: UseFeatureFlagOptions) => {
  const { clientRef, defaults, overrides, isReady } = useLaunchDarkly();
  const { current: client } = clientRef;

  const [allFeatureFlagsValues, setAllFeatureFlagsValues] = useState<
    LDFlagSet | undefined
  >(client?.allFlags?.());

  const prevIsReady = usePrevious(isReady);
  const shouldGetActualValues = !prevIsReady && isReady;
  const shouldListenForChanges = options?.listenForChanges;

  const updateFeatureFlags = useCallback((updatedFeatureFlags: any) => {
    setAllFeatureFlagsValues((currentFeatureFlags) => {
      return { ...currentFeatureFlags, ...updatedFeatureFlags };
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────

  // retrieve the actual feature flags values, after initializing the client
  useEffect(() => {
    if (!shouldGetActualValues) return;

    setAllFeatureFlagsValues(client?.allFlags());
  }, [client, shouldGetActualValues]);

  // listen for changes
  useEffect(() => {
    if (!client || !shouldListenForChanges) return;

    client.on('change', updateFeatureFlags);

    return () => {
      client.off('change', updateFeatureFlags);
    };
  }, [client, shouldListenForChanges, updateFeatureFlags]);

  // ─────────────────────────────────────────────────────────────────

  return useMemo<FeatureFlags>(
    () => ({ ...defaults, ...allFeatureFlagsValues, ...overrides }),
    [allFeatureFlagsValues, defaults, overrides],
  );
};

/**
 * Extends the functionality of the `useAllFeatureFlags` hook for advanced use cases.
 *
 * It returns an object with the following properties:
 *
 * - `values`: all feature flags values.
 * - `isStale`: whether the feature flags were returned by the initialized client.
 */
export const useAllFeatureFlagsAsync = (options?: UseFeatureFlagOptions) => {
  const { isReady } = useLaunchDarkly();

  const values = useAllFeatureFlags(options);
  const isStale = !isReady;

  return useMemo<UseAllFeatureFlagAsyncReturnValue>(
    () => ({ values, isStale }),
    [isStale, values],
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseFeatureFlagAsyncReturnValue<
  Key extends FeatureFlagKey,
  Value = FeatureFlagValue<Key>,
> = {
  readonly value: Value;
  readonly isStale: boolean;
  readonly isOverridden: boolean;
};

type UseAllFeatureFlagAsyncReturnValue = {
  readonly values: LDFlagSet;
  readonly isStale: boolean;
};

type UseFeatureFlagOptions = {
  readonly listenForChanges: boolean;
};
